// Layouts/Home/Dashboard.tsx
// GET /v1/dashboard?date_from&date_to        today's work + the period (admin: agency, staff: own leads)
// GET /v1/dashboard/staff?date_from&date_to  per staff member (admins only)
// GET /v1/leads-activity/stats               pipeline split (every status)
// GET /v1/leads?follow_up=due&limit=6        follow-ups due today + overdue
// GET /v1/leads-activity/recent-activity?limit=5
// GET /v1/leads-activity/recently-contacted?limit=5&page=1
//
// Layout:
//   - header row: period switch (This month / 30 days / Last month) + refresh
//   - headline strip: the period's numbers in one bordered card
//   - "needs attention" tiles that open the Leads list already filtered
//   - activity chart (leads / calls / converted per day) + follow-ups due list
//   - pipeline donut + ranked stage list (all statuses, click to filter)
//   - recent activity timeline + recently contacted roster
//   - sources + team performance (admins)
// Every panel is live data — no sample numbers.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Bar,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  Phone,
  FileText,
  Users,
  AlarmClock,
  Activity,
  RefreshCw,
  ChevronRight,
  CloudOff,
  AlertTriangle,
  Sparkles,
  UserPlus,
  BadgeCheck,
  Trophy,
  Target,
} from 'lucide-react';
import { G, C } from '../components/GlobalComponents/uiTokens';
import apiClient from '../stateManagers/apiSetup/axiosInterceptor';

// ── TYPES ─────────────────────────────────────────────────────────────────────
type StageKey =
  | 'new'
  | 'contacted'
  | 'interested'
  | 'follow_up'
  | 'qualified'
  | 'converted'
  | 'nurture'
  | 'not_interested'
  | 'invalid';

type LeadStats = Record<StageKey, number> & { total: number; pending_conversions?: number };

interface DashboardData {
  scope: 'agency' | 'mine';
  now: {
    uncontacted: number;
    follow_ups_today: number;
    follow_ups_overdue: number;
    unassigned?: number;
    pending_conversions?: number;
  };
  period: { date_from: string; date_to: string; leads_in: number; calls: number; converted: number; conversion_rate: number };
  trend: { day: string; leads: number; calls?: number; converted: number }[];
  by_source: { source: string; leads: number; converted: number }[];
}

interface StaffStat {
  staff_id: string;
  email: string;
  open: number;
  new: number;
  follow_ups: number;
  assigned: number;
  calls: number;
  converted: number;
}

interface FollowUpLead {
  id: string;
  name: string | null;
  phone: string | null;
  status: string | null;
  follow_up_at?: string | null;
  assigned_to?: { id: string; email?: string | null } | null;
}

interface ActivityItem {
  lead_id: string;
  name: string | null;
  status: string | null;
  latest_activity: {
    type: string;
    text: string | null;
    outcome: string | null;
    extra_note: string | null;
    created_at: string | null;
  } | null;
}

interface ContactedLead {
  lead_id: string;
  name: string | null;
  phone: string | null;
  source: string | null;
  status: string | null;
  city: string | null;
  last_call: { text: string | null; outcome: string | null; called_at: string | null } | null;
}

// ── SHELL / INNER (matches LeadDetailView / Sidebar chrome — grey outer border) ─
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  boxSizing: 'border-box',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  boxSizing: 'border-box',
};
const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={SHELL}>
    <div style={{ ...INNER, ...style }}>{children}</div>
  </div>
);

// Same order and colours as the Leads page statuses.
const STAGES: { key: StageKey; label: string; color: string }[] = [
  { key: 'new', label: 'New', color: '#3b82f6' },
  { key: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { key: 'interested', label: 'Interested', color: '#14b8a6' },
  { key: 'follow_up', label: 'Follow up', color: '#eab308' },
  { key: 'qualified', label: 'Qualified', color: '#8b5cf6' },
  { key: 'converted', label: 'Converted', color: '#22c55e' },
  { key: 'nurture', label: 'Nurture', color: '#64748b' },
  { key: 'not_interested', label: 'Not interested', color: '#ef4444' },
  { key: 'invalid', label: 'Invalid', color: '#9ca3af' },
];
const stageOf = (s: string | null) => STAGES.find((x) => x.key === (s ?? '').toLowerCase());

// Activity chart series (validated: lightness, chroma, colour-blind separation, contrast)
const SERIES = {
  leads: { label: 'Leads in', color: '#00897b' },
  calls: { label: 'Calls', color: '#7a9a00' },
  converted: { label: 'Converted', color: '#2563eb' },
};

const ACTIVITY_TYPES: Record<string, { icon: any; color: string; label: string }> = {
  call_log: { icon: Phone, color: '#2563eb', label: 'Call logged' },
  note: { icon: FileText, color: '#6b8e12', label: 'Note added' },
  meeting: { icon: Users, color: '#c2700c', label: 'Meeting' },
  follow_up: { icon: AlarmClock, color: '#7c3aed', label: 'Follow-up' },
};
const DEFAULT_TYPE = { icon: Activity, color: C.teal, label: 'Activity' };

const statusStyle = (s: string | null) => {
  const st = stageOf(s);
  return st ? { bg: `${st.color}1f`, color: st.color === '#9ca3af' ? '#4b5563' : st.color } : { bg: 'rgba(156,163,175,0.12)', color: '#4b5563' };
};

const AVATAR_COLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777'];
const avc = (s: string | null) => {
  if (!s) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initialsOf = (name: string | null) => {
  const clean = (name ?? '').trim();
  if (!clean) return '?';
  const p = clean.split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : clean.slice(0, 2)).toUpperCase();
};
const relativeTime = (value: string | null) => {
  if (!value) return '';
  let iso = value.includes('T') ? value : value.replace(' ', 'T');
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) iso += 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// ── Days (IST) ───────────────────────────────────────────────────────────────
const istDay = (ms: number) => new Date(ms + 330 * 60000).toISOString().slice(0, 10);
const niceDay = (day: string) =>
  new Date(day + 'T00:00:00Z').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });

type RangeKey = 'month' | 'last30' | 'lastMonth';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'month', label: 'This month' },
  { key: 'last30', label: '30 days' },
  { key: 'lastMonth', label: 'Last month' },
];
function rangeFor(k: RangeKey): { date_from: string; date_to: string } {
  const today = istDay(Date.now());
  if (k === 'month') return { date_from: today.slice(0, 8) + '01', date_to: today };
  if (k === 'last30') return { date_from: istDay(Date.now() - 29 * 86400000), date_to: today };
  const [y, m] = today.split('-').map(Number);
  const last = new Date(Date.UTC(y, m - 1, 0));
  const first = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), 1));
  return { date_from: first.toISOString().slice(0, 10), date_to: last.toISOString().slice(0, 10) };
}

const followUpInfo = (iso?: string | null) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const day = istDay(t);
  const today = istDay(Date.now());
  if (day === today) return { text: 'Today', color: '#1d4ed8', bg: 'rgba(29,78,216,0.08)' };
  if (day < today) return { text: `Overdue · ${niceDay(day)}`, color: '#c62828', bg: 'rgba(239,68,68,0.08)' };
  return { text: niceDay(day), color: '#4a6865', bg: 'rgba(17,85,80,0.07)' };
};

// ── fetch hook (reloads when url / params / refresh tick change) ───────────────
function useDashboardFetch<T>(url: string | null, params: Record<string, any> | undefined, tick: number) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const key = JSON.stringify(params ?? {});

  const load = useCallback(
    async (live: { v: boolean } = { v: true }) => {
      if (!url) return; // waiting (e.g. until we know admin vs staff)
      setLoading(true);
      setError(false);
      try {
        const res = await apiClient.get(url, { params: JSON.parse(key) });
        if (!live.v) return;
        if (res.data.success) setData(res.data.data as T);
        else setError(true);
      } catch {
        if (live.v) setError(true);
      } finally {
        if (live.v) setLoading(false);
      }
    },
    [url, key]
  );

  useEffect(() => {
    const live = { v: true };
    load(live);
    return () => {
      live.v = false;
    };
  }, [load, tick]);

  return { data, loading: loading && data === null, error, reload: () => load() };
}

// ── small atoms ────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ icon: any; title: string; hint?: string }> = ({ icon: Icon, title, hint }) => (
  <div style={{ textAlign: 'center', padding: '34px 22px' }}>
    <div style={{ width: 44, height: 44, borderRadius: 13, background: C.limeDim, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9.2px' }}>
      <Icon size={20} color={C.teal} />
    </div>
    <div style={{ fontSize: '0.81rem', fontWeight: 700, color: C.teal }}>{title}</div>
    {hint && <div style={{ fontSize: '0.74rem', color: C.muted, marginTop: 4 }}>{hint}</div>}
  </div>
);

const ErrorState: React.FC<{ what: string; onRetry: () => void }> = ({ what, onRetry }) => (
  <div style={{ textAlign: 'center', padding: '34px 22px' }}>
    <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9.2px' }}>
      <CloudOff size={20} color="#c62828" />
    </div>
    <div style={{ fontSize: '0.81rem', fontWeight: 700, color: C.teal }}>Couldn't load {what}</div>
    <button
      onClick={onRetry}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5.5, marginTop: 11, padding: '6.5px 14px', background: 'rgba(17,85,80,0.07)', border: 'none', borderRadius: 20, color: C.teal, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
    >
      <RefreshCw size={12} /> Try again
    </button>
  </div>
);

const Skeleton: React.FC<{ h: number; m?: number }> = ({ h, m = 0 }) => (
  <div className="dsh-sk" style={{ height: h, borderRadius: 8, background: '#eef2f1', margin: m }} />
);

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  const s = statusStyle(status);
  return (
    <span style={{ padding: '1.8px 7.4px', borderRadius: 20, fontSize: '0.62rem', fontWeight: 700, textTransform: 'capitalize', background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {stageOf(status)?.label ?? (status ? status.replace(/_/g, ' ') : 'Unknown')}
    </span>
  );
};

const SectionHead: React.FC<{ title: string; pill?: string; onPill?: () => void; tag?: string }> = ({ title, pill, onPill, tag }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '24px 0 9px', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7.4, minWidth: 0 }}>
      <div style={{ width: 3, height: 14.8, background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: 3, flexShrink: 0 }} />
      <span style={{ fontSize: '0.86rem', fontWeight: 700, color: C.teal }}>{title}</span>
      {tag && (
        <span style={{ fontSize: '0.6rem', fontWeight: 700, color: C.sub, background: '#eef2f1', padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>{tag}</span>
      )}
    </div>
    {pill && (
      <button
        onClick={onPill}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 2.8, padding: '4.6px 11px', background: 'rgba(17,85,80,0.07)', border: 'none', borderRadius: 20, color: C.teal, fontSize: '0.71rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        {pill} <ChevronRight size={11} />
      </button>
    )}
  </div>
);

const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid ${G.line}`, borderRadius: 8, padding: '8px 10px', fontSize: '0.72rem', boxShadow: '0 6px 18px rgba(17,85,80,0.12)' }}>
      <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{niceDay(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.sub }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          {SERIES[p.dataKey as keyof typeof SERIES]?.label}
          <b style={{ marginLeft: 'auto', paddingLeft: 12, color: '#1f2937' }}>{p.value}</b>
        </div>
      ))}
    </div>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────────
interface DashboardProps {
  onNavigateToLeads?: () => void;
  onOpenLead?: (leadId: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigateToLeads, onOpenLead }) => {
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeKey>('month');
  const [tick, setTick] = useState(0);
  const period = useMemo(() => rangeFor(range), [range]);

  const dash = useDashboardFetch<DashboardData>('/v1/dashboard', period, tick);
  const isAdmin = dash.data?.scope === 'agency';
  const staff = useDashboardFetch<StaffStat[]>(isAdmin ? '/v1/dashboard/staff' : null, period, tick);
  const stats = useDashboardFetch<LeadStats>('/v1/leads-activity/stats', undefined, tick);
  // staff: only their own follow-ups — so wait for /dashboard to say who is looking
  const due = useDashboardFetch<FollowUpLead[]>(
    dash.data ? '/v1/leads' : null,
    { follow_up: 'due', limit: 6, page: 1, ...(dash.data && !isAdmin ? { assigned: 'me' } : {}) },
    tick
  );
  const activity = useDashboardFetch<ActivityItem[]>('/v1/leads-activity/recent-activity', { limit: 5 }, tick);
  const contacted = useDashboardFetch<ContactedLead[]>('/v1/leads-activity/recently-contacted', { limit: 5, page: 1 }, tick);

  const openLead = (id: string) => (onOpenLead ? onOpenLead(id) : navigate(`/leads/${id}/details`));
  const openLeads = (query = '') => (query ? navigate(`/leads?${query}`) : onNavigateToLeads ? onNavigateToLeads() : navigate('/leads'));

  const s = stats.data;
  const total = s?.total ?? 0;
  const donutData = STAGES.map((st) => ({ name: st.label, value: s?.[st.key] ?? 0, color: st.color })).filter((d) => d.value > 0);
  const ranked = [...STAGES].sort((a, b) => (s?.[b.key] ?? 0) - (s?.[a.key] ?? 0));

  const p = dash.data?.period;
  const n = dash.data?.now;
  const trend = dash.data?.trend ?? [];
  const trendHasData = trend.some((d) => d.leads || d.calls || d.converted);
  const sources = dash.data?.by_source ?? [];
  const srcMax = Math.max(1, ...sources.map((x) => x.leads));
  const teamTop = useMemo(() => (staff.data ?? []).slice(0, 8), [staff.data]);

  const attention = n
    ? [
        { key: 'today', label: 'Follow-ups today', value: n.follow_ups_today, icon: AlarmClock, color: '#1d4ed8', bg: 'rgba(29,78,216,0.08)', go: () => openLeads('follow_up=today') },
        { key: 'overdue', label: 'Overdue follow-ups', value: n.follow_ups_overdue, icon: AlertTriangle, color: '#c62828', bg: 'rgba(239,68,68,0.08)', go: () => openLeads('follow_up=overdue') },
        { key: 'new', label: 'Not contacted', value: n.uncontacted, icon: Sparkles, color: C.teal, bg: 'rgba(17,85,80,0.07)', go: () => openLeads('status=new') },
        ...(isAdmin
          ? [
              { key: 'unassigned', label: 'Unassigned', value: n.unassigned ?? 0, icon: UserPlus, color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', go: () => openLeads('assigned=none') },
              { key: 'pending', label: 'Conversions to approve', value: n.pending_conversions ?? 0, icon: BadgeCheck, color: '#b45309', bg: 'rgba(245,158,11,0.12)', go: () => openLeads('conversion=pending') },
            ]
          : []),
      ]
    : [];

  return (
    <div className="dsh">
      <style>{`
        .dsh-tile { transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease; }
        .dsh-tile:hover { transform: translateY(-1px); border-color: ${C.teal}33 !important; box-shadow: 0 6px 16px rgba(17,85,80,0.08); }
        .dsh-row:hover { background: ${G.shellBg}; }
        .dsh-sk { animation: dshPulse 1.2s ease-in-out infinite; }
        @keyframes dshPulse { 50% { opacity: .55; } }
        .dsh-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
        .dsh-grid-chart { display: grid; grid-template-columns: 1.55fr 1fr; gap: 11px; }
        .dsh-grid-hero { display: grid; grid-template-columns: 260px 1fr; gap: 11px; }
        .dsh-grid-team { display: grid; grid-template-columns: 1fr 1.55fr; gap: 11px; }
        .dsh-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 9px; }
        .dsh-strip { display: flex; align-items: stretch; flex-wrap: wrap; row-gap: 14px; }
        @media (max-width: 1000px) { .dsh-grid2, .dsh-grid-chart, .dsh-grid-hero, .dsh-grid-team { grid-template-columns: 1fr; } }
        @media (max-width: 700px) { .dsh-div { display: none; } .dsh-strip { gap: 16px 22px; } }
        @media (prefers-reduced-motion: reduce) { .dsh-tile, .dsh-sk { transition: none; animation: none; } }
      `}</style>

      {/* ── HEADER: period + refresh ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 11 }}>
        <div>
          <div style={{ fontSize: '1.02rem', fontWeight: 800, color: C.teal, letterSpacing: '-0.2px' }}>
            {dash.data && !isAdmin ? 'Your overview' : 'Overview'}
          </div>
          <div style={{ fontSize: '0.7rem', color: C.sub, marginTop: 2 }}>
            {p ? `${niceDay(p.date_from)} – ${niceDay(p.date_to)}` : ' '}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div role="tablist" style={{ display: 'flex', padding: 3, background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: 9 }}>
            {RANGES.map((r) => {
              const on = r.key === range;
              return (
                <button
                  key={r.key}
                  role="tab"
                  aria-selected={on}
                  onClick={() => setRange(r.key)}
                  style={{ padding: '6px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: '0.71rem', fontWeight: 700, background: on ? C.teal : 'transparent', color: on ? '#fff' : C.sub }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setTick((t) => t + 1)}
            aria-label="Refresh dashboard"
            title="Refresh"
            style={{ width: 34, height: 34, borderRadius: 9, border: `1px solid ${G.shellBorder}`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <RefreshCw size={14} color={C.teal} />
          </button>
        </div>
      </div>

      {/* ── HEADLINE STRIP: the period in one bordered card ── */}
      <Panel style={{ padding: '18px 22px' }}>
        {dash.loading ? (
          <Skeleton h={40} />
        ) : dash.error && !dash.data ? (
          <ErrorState what="the overview" onRetry={dash.reload} />
        ) : (
          <div className="dsh-strip">
            {[
              { label: 'Leads in', value: p?.leads_in ?? 0 },
              { label: 'Calls logged', value: p?.calls ?? 0 },
              { label: 'Converted', value: p?.converted ?? 0 },
              { label: 'Conversion rate', value: `${p?.conversion_rate ?? 0}%`, hint: 'converted ÷ leads in' },
              { label: 'Total leads', value: stats.data ? total : '—' },
            ].map((m, i) => (
              <React.Fragment key={m.label}>
                {i > 0 && <div className="dsh-div" style={{ width: 1, alignSelf: 'stretch', background: G.line, margin: '0 24px' }} />}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 96 }} title={m.hint}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 600, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{m.label}</div>
                  <span style={{ fontSize: '1.32rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.3px' }}>{m.value}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </Panel>

      {/* ── NEEDS ATTENTION: click a tile → Leads, already filtered ── */}
      <SectionHead title="Needs attention" tag="right now" />
      {dash.loading ? (
        <Skeleton h={78} />
      ) : (
        <div className="dsh-tiles">
          {attention.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                className="dsh-tile"
                onClick={t.go}
                aria-label={`${t.label}: ${t.value}`}
                style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 14px', background: '#fff', border: `1px solid ${G.line}`, borderRadius: 11, cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={17} color={t.color} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: t.value ? t.color : '#1f2937', lineHeight: 1.1 }}>{t.value}</div>
                  <div style={{ fontSize: '0.68rem', color: C.sub, fontWeight: 600, lineHeight: 1.3 }}>{t.label}</div>
                </div>
                <ChevronRight size={14} color={C.muted} />
              </button>
            );
          })}
        </div>
      )}

      {/* ── ACTIVITY CHART + FOLLOW-UPS DUE ── */}
      <div className="dsh-grid-chart">
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <SectionHead title="Daily activity" />
          <Panel style={{ padding: '14px 16px 8px' }}>
            {dash.loading ? (
              <Skeleton h={220} />
            ) : !trendHasData ? (
              <EmptyState icon={Activity} title="Nothing in this period" hint="New leads, calls and conversions per day show up here." />
            ) : (
              <>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
                  {(Object.keys(SERIES) as (keyof typeof SERIES)[]).map((k) => (
                    <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', color: C.sub, fontWeight: 600 }}>
                      <span style={{ width: k === 'converted' ? 14 : 9, height: k === 'converted' ? 2 : 9, borderRadius: 2, background: SERIES[k].color }} />
                      {SERIES[k].label}
                    </span>
                  ))}
                </div>
                <div style={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={trend} barGap={2} barCategoryGap="22%" margin={{ top: 6, right: 4, left: -18, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={G.softer} />
                      <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10.5, fill: C.sub }}
                        tickFormatter={(d: string) => niceDay(d)}
                        minTickGap={18}
                      />
                      <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 10.5, fill: C.sub }} width={40} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(17,85,80,0.05)' }} />
                      <Bar dataKey="leads" fill={SERIES.leads.color} radius={[4, 4, 0, 0]} maxBarSize={14} />
                      <Bar dataKey="calls" fill={SERIES.calls.color} radius={[4, 4, 0, 0]} maxBarSize={14} />
                      <Line dataKey="converted" type="monotone" stroke={SERIES.converted.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </Panel>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <SectionHead
            title="Follow-ups due"
            tag={n ? `${n.follow_ups_today + n.follow_ups_overdue}` : undefined}
            pill="View all"
            onPill={() => openLeads('follow_up=due')}
          />
          <Panel style={{ padding: '4px 0' }}>
            {due.loading ? (
              <Skeleton h={200} m={14} />
            ) : due.error && !due.data ? (
              <ErrorState what="follow-ups" onRetry={due.reload} />
            ) : !due.data || due.data.length === 0 ? (
              <EmptyState icon={AlarmClock} title="All caught up" hint="Set a next follow-up when you log a call and it shows up here." />
            ) : (
              due.data.map((l, i) => {
                const fu = followUpInfo(l.follow_up_at);
                const name = l.name?.trim() || 'Unnamed lead';
                return (
                  <div
                    key={l.id}
                    className="dsh-row"
                    onClick={() => openLead(l.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < due.data!.length - 1 ? `1px solid ${G.softer}` : 'none', cursor: 'pointer' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.77rem', fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, flexWrap: 'wrap' }}>
                        {fu && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1.5px 7px', borderRadius: 20, background: fu.bg, color: fu.color, fontSize: '0.6rem', fontWeight: 700 }}>
                            <AlarmClock size={9} /> {fu.text}
                          </span>
                        )}
                        <StatusBadge status={l.status} />
                        {isAdmin && (
                          <span style={{ fontSize: '0.6rem', color: C.sub }}>
                            {l.assigned_to?.email ? l.assigned_to.email.split('@')[0] : 'Unassigned'}
                          </span>
                        )}
                      </div>
                    </div>
                    {l.phone && (
                      <button
                        aria-label={`Call ${name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`tel:${l.phone!.replace(/[^\d+]/g, '')}`);
                        }}
                        style={{ width: 30, height: 30, borderRadius: 9, background: C.limeDim, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                      >
                        <Phone size={13} color="#4a5e00" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </Panel>
        </div>
      </div>

      {/* ── PIPELINE: donut + ranked stage list (click a stage → Leads) ── */}
      <SectionHead title="Pipeline breakdown" tag="all time" pill="View all leads" onPill={() => openLeads()} />
      <div className="dsh-grid-hero">
        <Panel style={{ padding: '18px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {stats.loading ? (
            <div className="dsh-sk" style={{ width: 150, height: 150, borderRadius: '50%', background: '#eef2f1' }} />
          ) : !s || total === 0 ? (
            <EmptyState icon={Activity} title="No leads yet" hint="Your pipeline split will show up here." />
          ) : (
            <>
              <div style={{ position: 'relative', width: 170, height: 170 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" innerRadius={58} outerRadius={78} paddingAngle={2} stroke="#fff" strokeWidth={2}>
                      {donutData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any, name: any) => [`${v} leads`, name]} contentStyle={{ borderRadius: 8, border: `1px solid ${G.line}`, fontSize: '0.72rem' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.4px' }}>{total}</span>
                  <span style={{ fontSize: '0.63rem', fontWeight: 600, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Total</span>
                </div>
              </div>
              {(s.pending_conversions ?? 0) > 0 && (
                <div style={{ marginTop: 10, fontSize: '0.66rem', fontWeight: 700, color: '#b45309', textAlign: 'center' }}>
                  {s.pending_conversions} conversion{s.pending_conversions === 1 ? '' : 's'} awaiting approval
                </div>
              )}
            </>
          )}
        </Panel>

        <Panel style={{ padding: '6px 4px' }}>
          {stats.error && !s ? (
            <ErrorState what="lead stats" onRetry={stats.reload} />
          ) : stats.loading ? (
            <Skeleton h={200} m={12} />
          ) : !s || total === 0 ? (
            <EmptyState icon={Activity} title="Nothing to rank yet" />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
              {ranked.map((st) => {
                const count = s[st.key] ?? 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div
                    key={st.key}
                    className="dsh-row"
                    onClick={() => openLeads(`status=${st.key}`)}
                    title={`Open ${st.label} leads`}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', borderBottom: `1px solid ${G.softer}`, cursor: 'pointer', opacity: count ? 1 : 0.55 }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: st.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#1f2937', width: 104, flexShrink: 0 }}>{st.label}</span>
                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#eef2f1', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: st.color, borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                    <span style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, width: 34, textAlign: 'right', flexShrink: 0 }}>{count}</span>
                    <span style={{ fontSize: '0.66rem', color: C.sub, width: 32, textAlign: 'right', flexShrink: 0 }}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* ── ACTIVITY FEED (timeline rail) + CONTACTED ROSTER (plain list) ── */}
      <div className="dsh-grid2">
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <SectionHead title="Recent activity" />
          <Panel style={{ padding: '16px 18px' }}>
            {activity.loading ? (
              <Skeleton h={140} />
            ) : activity.error && !activity.data ? (
              <ErrorState what="recent activity" onRetry={activity.reload} />
            ) : !activity.data || activity.data.length === 0 ? (
              <EmptyState icon={Activity} title="No activity yet" hint="Calls, notes and follow-ups appear here." />
            ) : (
              activity.data.map((item, i) => {
                const act = item.latest_activity;
                const type = ACTIVITY_TYPES[act?.type ?? ''] ?? DEFAULT_TYPE;
                const Icon = type.icon;
                const detail = act?.outcome || act?.text || act?.extra_note || '';
                const name = item.name?.trim() || 'Unnamed lead';
                const when = relativeTime(act?.created_at ?? null);
                const isLast = i === activity.data!.length - 1;

                return (
                  <div key={item.lead_id} style={{ display: 'flex', gap: 11 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 22, flexShrink: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: type.color, marginTop: 4, boxShadow: `0 0 0 3px ${type.color}1f` }} />
                      {!isLast && <div style={{ width: 1.5, flex: 1, background: G.line, minHeight: 30, marginTop: 3 }} />}
                    </div>
                    <div onClick={() => openLead(item.lead_id)} style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 4 : 16, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon size={12} color={type.color} />
                        <span style={{ fontSize: '0.71rem', fontWeight: 700, color: type.color }}>{type.label}</span>
                        {when && <span style={{ fontSize: '0.64rem', color: C.sub, marginLeft: 'auto' }}>{when}</span>}
                      </div>
                      <div style={{ fontSize: '0.79rem', fontWeight: 600, color: '#1f2937', margin: '3px 0 2px' }}>{name}</div>
                      {detail && (
                        <div style={{ fontSize: '0.7rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</div>
                      )}
                      <div style={{ marginTop: 5 }}>
                        <StatusBadge status={item.status} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </Panel>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <SectionHead title="Recently contacted" />
          <Panel style={{ padding: '4px 0' }}>
            {contacted.loading ? (
              <Skeleton h={140} m={14} />
            ) : contacted.error && !contacted.data ? (
              <ErrorState what="recent calls" onRetry={contacted.reload} />
            ) : !contacted.data || contacted.data.length === 0 ? (
              <EmptyState icon={Phone} title="No calls logged yet" hint="Leads you call and log show up here." />
            ) : (
              contacted.data.map((lead, i) => {
                const name = lead.name?.trim() || 'Unnamed lead';
                const outcome = lead.last_call?.outcome || lead.last_call?.text;
                const fallback = [lead.source, lead.city].filter(Boolean).join(' · ');
                const when = relativeTime(lead.last_call?.called_at ?? null);

                return (
                  <div
                    key={lead.lead_id}
                    className="dsh-row"
                    onClick={() => openLead(lead.lead_id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 18px', borderBottom: i < contacted.data!.length - 1 ? `1px solid ${G.softer}` : 'none', cursor: 'pointer' }}
                  >
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: avc(name), color: '#fff', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {initialsOf(name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                      <div style={{ fontSize: '0.68rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{outcome || fallback || 'No details'}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                      <StatusBadge status={lead.status} />
                      {when && <span style={{ fontSize: '0.62rem', color: C.sub }}>{when}</span>}
                    </div>
                    {lead.phone && (
                      <button
                        aria-label={`Call ${name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`tel:${lead.phone!.replace(/[^\d+]/g, '')}`);
                        }}
                        style={{ width: 30, height: 30, borderRadius: 9, background: C.limeDim, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                      >
                        <Phone size={13} color="#4a5e00" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </Panel>
        </div>
      </div>

      {/* ── SOURCES + TEAM PERFORMANCE ── */}
      <div className={isAdmin ? 'dsh-grid-team' : ''}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <SectionHead title="Lead sources" tag="this period" />
          <Panel style={{ padding: '8px 4px' }}>
            {dash.loading ? (
              <Skeleton h={160} m={12} />
            ) : sources.length === 0 ? (
              <EmptyState icon={Target} title="No leads in this period" />
            ) : (
              sources.map((x, i) => {
                const rate = x.leads ? Math.round((x.converted / x.leads) * 100) : 0;
                return (
                  <div key={x.source} style={{ padding: '9px 16px', borderBottom: i < sources.length - 1 ? `1px solid ${G.softer}` : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: '0.76rem', fontWeight: 700, color: '#1f2937', textTransform: 'capitalize', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {x.source.replace(/_/g, ' ')}
                      </span>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: C.teal }}>{x.leads}</span>
                      <span style={{ fontSize: '0.64rem', color: C.sub, width: 92, textAlign: 'right' }}>
                        {x.converted} conv. · {rate}%
                      </span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: '#eef2f1', overflow: 'hidden' }}>
                      <div style={{ width: `${(x.leads / srcMax) * 100}%`, height: '100%', background: SERIES.leads.color, borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })
            )}
          </Panel>
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <SectionHead title="Team performance" tag="calls & conversions this period" />
            <Panel style={{ padding: 0, overflowX: 'auto' }}>
              {staff.loading ? (
                <Skeleton h={160} m={12} />
              ) : staff.error && !staff.data ? (
                <ErrorState what="team numbers" onRetry={staff.reload} />
              ) : teamTop.length === 0 ? (
                <EmptyState icon={Users} title="No leads assigned yet" hint="Assign leads to staff and their numbers show up here." />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', minWidth: 520 }}>
                  <thead>
                    <tr style={{ background: G.shellBg }}>
                      {['Staff', 'Open', 'New', 'Due', 'Assigned', 'Calls', 'Converted'].map((h, i) => (
                        <th key={h} style={{ textAlign: i ? 'right' : 'left', padding: '9px 14px', fontSize: '0.62rem', fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${G.line}` }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teamTop.map((m, i) => {
                      const name = m.email ? m.email.split('@')[0] : 'Staff';
                      const best = i === 0 && m.converted > 0;
                      const num = (v: number, color?: string) => (
                        <td style={{ textAlign: 'right', padding: '9px 14px', fontWeight: 700, color: v ? color ?? '#1f2937' : C.muted, fontVariantNumeric: 'tabular-nums' }}>{v}</td>
                      );
                      return (
                        <tr key={m.staff_id} style={{ borderBottom: i < teamTop.length - 1 ? `1px solid ${G.softer}` : 'none' }}>
                          <td style={{ padding: '9px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                              <div style={{ width: 26, height: 26, borderRadius: '50%', background: avc(name), color: '#fff', fontSize: '0.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {initialsOf(name)}
                              </div>
                              <span style={{ fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }} title={m.email}>
                                {name}
                              </span>
                              {best && <Trophy size={12} color="#b45309" aria-label="Most conversions" />}
                            </div>
                          </td>
                          {num(m.open)}
                          {num(m.new, C.teal)}
                          {num(m.follow_ups, '#c62828')}
                          {num(m.assigned)}
                          {num(m.calls)}
                          {num(m.converted, '#15803d')}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
